/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import DataActionBaseComposer, { DataActionBaseComposerArgs } from 'cio/components/action-composer/data-action/base';
import { AttributePreview, CreateEventPreview } from 'cio/utils/previews';
import { action } from '@ember/object';
import type CampaignModel from 'cio/models/campaign';
import { tracked } from '@glimmer/tracking';
import DataActionBodyAttributeRows from 'cio/app-models/data-action/body-attribute-rows';
import { htmlSafe } from '@ember/template';
import {
  CreateEventEditorType,
  DataActionConditionType,
  DATA_ACTION_CONDITION_TYPE_LABEL,
  getSupportedConditionTypes,
} from 'cio/utils/campaigns/data-action';
import CreateEventActionEventName from 'cio/app-models/create-event/event-name';
import { Editor } from 'cio/services/editor';
import type AnalyticsService from 'cio/services/analytics';
import { inject as service } from '@ember/service';
import type RouterService from '@ember/routing/router-service';
import { BatchUpdateTemplateType } from 'cio/utils/actions/batch-update';
import { SerializedLegacyFilter } from 'cio/types/conditions/legacy-filter';
import { isEmpty } from '@ember/utils';

interface BatchUpdateComposerArgs extends DataActionBaseComposerArgs {
  preview: AttributePreview & CreateEventPreview;
  hasReadOnlyPermission: boolean;
}

interface BatchUpdateComposerSignature {
  Args: BatchUpdateComposerArgs;
  Element: HTMLDivElement;
}

function supportedConditionTypes(campaign: CampaignModel, templateType: BatchUpdateTemplateType | string | null) {
  let defaultTypes = getSupportedConditionTypes(campaign);

  if (templateType != null && templateType === BatchUpdateTemplateType.AttributeUpdate) {
    return [...defaultTypes, DataActionConditionType.RemoveAttribute];
  }

  return defaultTypes;
}

export default class BatchUpdateComposer extends DataActionBaseComposer<BatchUpdateComposerSignature> {
  @service analytics!: AnalyticsService;
  @service router!: RouterService;

  previewObserverKeys = [
    'args.editableEntity.template.{body,editor,name,templateType}',
    'body.isDirty',
    'conditions',
    'templateType',
  ];

  @tracked templateType: BatchUpdateTemplateType | string | null = this.template.templateType;
  @tracked isLoadingInitialMergeData = true;

  get supportedConditionTypes() {
    return supportedConditionTypes(this.args.editableEntity.campaign as CampaignModel, this.templateType);
  }

  get previewURL() {
    if (this.templateType === BatchUpdateTemplateType.CreateEvent) {
      return 'previews/create_event';
    } else if (this.templateType === BatchUpdateTemplateType.AttributeUpdate) {
      return 'previews/create_or_update_person';
    }

    return '';
  }

  constructor(owner: unknown, args: BatchUpdateComposerSignature['Args']) {
    super(owner, args);
    this.watchTemplate();
    this.deserializeRecipientConditions();
    this.deserializeEventName();
    this.deserializeBody();
  }

  getPreviewData() {
    let data: Record<string, any> = {};

    // Add fake customer data.
    // We allow "profile_attribute" conditions in batch update actions, but
    // there is no "sample customer" selection available.
    // We add fake data to the preview rendering so that there aren't any
    // misleading preview errors.
    if (this.args.editableEntity.campaign?.isDataCampaign) {
      let profileAttributesUsed =
        this.bodyRows?.rows.filter((r) => r.type === DataActionConditionType.ProfileAttribute).map((r) => r.value) ??
        [];
      data.variables = {
        customer: profileAttributesUsed.reduce<Record<string, string>>((acc, attribute) => {
          acc[attribute] = `sample ${attribute} preview`;
          return acc;
        }, {}),
      };
    }

    if (this.templateType === BatchUpdateTemplateType.CreateEvent) {
      data.body = this.template.editor === Editor.DEFAULT ? this.bodyRows?.serialize() : this.template.body;
      data.name = this.eventName?.serialize();
    } else if (this.templateType === BatchUpdateTemplateType.AttributeUpdate) {
      data.body = this.bodyRows?.serialize();
    }

    return data;
  }

  @tracked eventName?: CreateEventActionEventName;
  @tracked bodyRows?: DataActionBodyAttributeRows;
  @tracked conditions: SerializedLegacyFilter[] = [];

  private deserializeRecipientConditions() {
    try {
      this.conditions = JSON.parse(this.template.recipient as string) ?? [];
    } catch (e) {
      this.conditions = [];
    }
  }

  private deserializeEventName() {
    this.eventName = CreateEventActionEventName.deserialize(this.template.name as string);
  }

  private deserializeBody() {
    if (this.templateType === BatchUpdateTemplateType.CreateEvent) {
      this.bodyRows = DataActionBodyAttributeRows.deserialize(this.template.body, {
        defaultType: this.defaultConditionType,
      });
    } else if (this.templateType === BatchUpdateTemplateType.AttributeUpdate) {
      this.bodyRows = DataActionBodyAttributeRows.deserialize(this.template.body, {
        defaultType: this.defaultConditionType,
        minimumRowsCount: 0,
        actionType: 'attribute_update_action',
      });
    } else {
      this.bodyRows = undefined;
    }
  }

  get isTemplateTypeDirty() {
    // For a newly created action, we don't want to show the save bar when it's
    // just the template type that changed.
    if (this.template.templateType == null || isEmpty(this.template.templateType)) {
      return false;
    }

    // We only show a dirty state based on template type if it changes from a
    // non-empty state
    return !isEmpty(this.conditions) && this.template.templateType !== this.templateType;
  }

  get isDirty() {
    return (
      this.template.get('hasDirtyAttributes') ||
      this.isTemplateTypeDirty ||
      (this.bodyRows?.isDirty ?? false) ||
      (this.templateType === BatchUpdateTemplateType.CreateEvent && (this.eventName?.isDirty ?? false))
    );
  }

  get isInvalid() {
    if (this.templateType === BatchUpdateTemplateType.CreateEvent) {
      if (!this.eventName?.isValid) {
        return true;
      }
    }

    if (this.bodyRows == null) {
      return false;
    }

    return this.bodyRows.hasDuplicateRowNames || this.bodyRows.hasErrors || this.bodyRows.hasIllegalEmptyValues;
  }

  get showSaveTooltip() {
    return this.saveTooltipContent !== false;
  }

  get saveTooltipContent() {
    if (this.templateType === BatchUpdateTemplateType.CreateEvent) {
      if (!this.eventName?.isValid) {
        return 'You need to add an event name';
      }
    }

    if (this.bodyRows?.hasIllegalEmptyValues) {
      return htmlSafe(
        `Enter a value for all selected attributes, or use the <b>${
          DATA_ACTION_CONDITION_TYPE_LABEL[DataActionConditionType.RemoveAttribute]
        }</b> value type`,
      );
    }

    return false;
  }

  get allowUnfocusedLiquidInsertion() {
    let hasValidTemplateType =
      this.templateType != null &&
      !isEmpty(this.templateType) &&
      (Object.values(BatchUpdateTemplateType) as string[]).includes(this.templateType);
    let hasConditions = this.conditions != null && this.conditions.length > 0;

    return hasValidTemplateType && hasConditions;
  }

  private serializeRecipientConditions() {
    if (this.conditions.length === 0) {
      this.template.recipient = null;
      return;
    }

    this.template.recipient = JSON.stringify(this.conditions);
  }

  private serializeCreateEventFields() {
    if (isEmpty(this.conditions)) {
      this.template.name = '';
    } else {
      this.template.name = this.eventName?.serialize() ?? '';
    }

    if (this.template.editor === Editor.DEFAULT) {
      this.serializeBody();
    }
  }

  private serializeAttributeUpdateFields() {
    this.template.name = '';
    this.serializeBody();
  }

  private serializeBody() {
    if (isEmpty(this.conditions)) {
      this.template.body = '';
    } else {
      this.template.body = this.bodyRows?.serialize() ?? '';
    }
  }

  @action
  async save() {
    if (this.isInvalid) {
      return;
    }

    if (this.templateType === BatchUpdateTemplateType.CreateEvent) {
      this.serializeCreateEventFields();
    } else if (this.templateType === BatchUpdateTemplateType.AttributeUpdate) {
      this.serializeAttributeUpdateFields();
    }

    if (this.templateType != null) {
      this.template.templateType = this.templateType;
    }

    await this.args.onSave();
    this.deserializeEventName();
    this.deserializeBody();
  }

  @action
  rollback() {
    this.template.rollbackAttributes();
    this.templateType = this.template.templateType;
    this.deserializeRecipientConditions();
    this.deserializeEventName();
    this.deserializeBody();
    this.args.onDirtyChange?.(false);

    this.args.onRollback();
  }

  @action
  onDirtyChange(_element: HTMLElement, [isDirty]: [boolean]) {
    this.args.onDirtyChange?.(isDirty);
  }

  @action
  onShowSaveButtonTooltip() {
    if (!this.isInvalid) {
      return;
    }

    this.analytics.track('user', 'error_encountered', {
      currentUrl: this.router.currentURL,
      message: this.saveTooltipContent,
      source: 'Batch Update',
    });
  }

  @action
  convertBodyBetweenTemplateTypes(sourceType: BatchUpdateTemplateType, targetType: BatchUpdateTemplateType) {
    if (this.bodyRows == null) {
      return;
    }

    // If the source is an attribute update and we're moving to send event, we
    // need to strip out "remove_attribute" rows.
    let campaign = this.args.editableEntity.campaign as CampaignModel;
    let sourceConditionTypes = supportedConditionTypes(campaign, sourceType);
    let targetConditionTypes = supportedConditionTypes(campaign, targetType);

    let unsupportedTargetTypes = sourceConditionTypes.filter((type) => !targetConditionTypes.includes(type));

    if (unsupportedTargetTypes.length > 0) {
      this.bodyRows.rows = this.bodyRows.rows.filter((row) => !unsupportedTargetTypes.includes(row.type));
    }
  }

  @action
  selectTemplateType(type: BatchUpdateTemplateType) {
    let currentType = this.templateType;

    this.templateType = type;

    if (currentType != null && !isEmpty(currentType)) {
      this.convertBodyBetweenTemplateTypes(currentType as BatchUpdateTemplateType, type);
      this.serializeBody();
    }

    // Switching types should reset "template.editor" value https://github.com/customerio/opsbugs/issues/4113
    this.template.editor = Editor.DEFAULT;

    this.deserializeEventName();
    this.deserializeBody();
  }

  @action
  switchEditor(editor: CreateEventEditorType) {
    this.template.editor = editor;

    if (editor === Editor.DEFAULT) {
      this.deserializeBody();
    }
  }

  @action
  updateConditions(conditions: SerializedLegacyFilter[]) {
    this.conditions = conditions;

    this.serializeRecipientConditions();
  }

  @action
  onInitialMergeDataLoadFinished() {
    this.isLoadingInitialMergeData = false;
    this.fetchPreview();
  }
}
